
import { useState, useCallback } from 'react';
import { supabase, BUCKETS, initializeBuckets, getCorrectPublicUrl } from '@/utils/supabase';
import { toast } from '@/hooks/use-toast';
import { hasValidSupabaseConfig } from '@/utils/supabaseDebug';

export const useStorage = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initializationAttempts, setInitializationAttempts] = useState(0);
  const [bucketCreationAttempted, setBucketCreationAttempted] = useState(false);

  const useLocalStorageOnly = useCallback(() => {
    return localStorage.getItem('useLocalStorageOnly') === 'true';
  }, []);

  const setUseLocalStorageOnly = useCallback((value: boolean) => {
    localStorage.setItem('useLocalStorageOnly', value.toString());
  }, []);

  const initializeStorage = useCallback(async (force = false, skipToast = false) => {
    if (isInitializing && !force) return { success: false, message: "Already initializing", hasRlsLimitations: false };
    
    if (useLocalStorageOnly() && !force) {
      console.log("Using local storage only mode due to previous failures");
      return { success: false, message: "Using local storage only mode", hasRlsLimitations: false };
    }
    
    try {
      setIsInitializing(true);
      setInitializationAttempts(prev => prev + 1);
      
      console.log(`Attempting to initialize storage (attempt ${initializationAttempts + 1})...`);
      
      if (!hasValidSupabaseConfig()) {
        console.warn('Invalid Supabase configuration, falling back to local storage');
        if (!skipToast) {
          toast({
            title: "Initialiseringsfel",
            description: "Saknad eller ogiltig Supabase-konfiguration. Kontrollera dina miljövariabler.",
            variant: "destructive"
          });
        }
        setIsInitializing(false);
        setUseLocalStorageOnly(true);
        return { 
          success: false, 
          message: "Missing or invalid Supabase configuration",
          hasRlsLimitations: false 
        };
      }
      
      if (bucketCreationAttempted && !force) {
        console.log("Bucket creation previously attempted and likely failed due to RLS, skipping");
        setIsInitialized(true);
        setIsInitializing(false);
        return {
          success: true,
          message: "Using basic connectivity with localStorage fallback for uploads",
          hasRlsLimitations: true
        };
      }
      
      try {
        const result = await initializeBuckets();
        
        if (result.success) {
          console.log("Storage connection successful:", result);
          setIsInitialized(true);
          setIsInitializing(false);
          
          if (!skipToast) {
            toast({
              title: "Framgång",
              description: "Anslutningen till Supabase-lagring lyckades.",
            });
          }
          
          setUseLocalStorageOnly(false);
          return result; // Return full result including any RLS limitations
        } else {
          const errorMessage = result.error?.message || "Could not initialize storage";
          console.error("Storage initialization failed:", errorMessage);
          
          if (errorMessage.includes("row-level security policy") || errorMessage.includes("policy")) {
            setIsInitialized(true);
            setBucketCreationAttempted(true);
            
            if (!skipToast) {
              toast({
                title: "Varning",
                description: "RLS-policy begränsar vissa funktioner. App fortsätter fungera med begränsad funktionalitet.",
                variant: "default"
              });
            }
            
            return { 
              success: true,
              message: "RLS policy detected. App will function with limited capabilities.",
              hasRlsLimitations: true
            };
          } else {
            if (!skipToast) {
              toast({
                title: "Initialiseringsfel",
                description: "Kunde inte förbereda lagringsutrymme. Använder lokal lagring istället.",
                variant: "destructive"
              });
            }
            
            setUseLocalStorageOnly(true);
            return { 
              success: false, 
              message: errorMessage, 
              error: result.error,
              hasRlsLimitations: false 
            };
          }
        }
      } catch (error) {
        console.error("Complete failure during initialization:", error);
        setUseLocalStorageOnly(true);
        
        if (!skipToast) {
          toast({
            title: "Initialiseringsfel",
            description: "Använder lokal lagring istället.",
            variant: "destructive"
          });
        }
        
        return { 
          success: false, 
          error,
          message: "Initialization error",
          hasRlsLimitations: false 
        };
      }
    } catch (error: any) {
      console.error("Exception during storage initialization:", error);
      setIsInitialized(false);
      setIsInitializing(false);
      setUseLocalStorageOnly(true);
      
      if (!skipToast) {
        toast({
          title: "Initialiseringsfel",
          description: "Ett oväntat fel inträffade. Använder lokal lagring istället.",
          variant: "destructive"
        });
      }
      
      return { 
        success: false, 
        error,
        message: "Unexpected error during initialization",
        hasRlsLimitations: false
      };
    } finally {
      setIsInitializing(false);
    }
  }, [initializationAttempts, isInitializing, useLocalStorageOnly, setUseLocalStorageOnly]);

  const uploadFile = async (file: File, bucket: string, fileName?: string): Promise<{ url: string; path: string; success: boolean; error?: any }> => {
    try {
      console.log(`Attempting to upload file to ${bucket}:`, fileName || file.name);
      
      if (useLocalStorageOnly()) {
        console.log("Using local storage for file upload (Supabase upload skipped)");
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const localPath = `local_${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
            const result = {
              url: reader.result as string,
              path: localPath,
              success: true
            };
            try {
              const localFiles = JSON.parse(localStorage.getItem('localFiles') || '{}');
              localFiles[localPath] = {
                url: reader.result,
                name: file.name,
                type: file.type,
                bucket,
                createdAt: new Date().toISOString()
              };
              localStorage.setItem('localFiles', JSON.stringify(localFiles));
            } catch (e) {
              console.warn("Could not save file to localStorage", e);
            }
            resolve(result);
          };
          reader.readAsDataURL(file);
        });
      }
      
      if (!isInitialized) {
        console.log("Storage not initialized, attempting to initialize before upload...");
        const initResult = await initializeStorage(true);
        
        if (!initResult.success && !initResult.hasRlsLimitations) {
          console.log("Failed to initialize buckets, using localStorage fallback");
          setUseLocalStorageOnly(true);
          return uploadFile(file, bucket, fileName);
        }
      }
      
      const actualFileName = fileName || `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      
      console.log(`Uploading to bucket ${bucket}:`, actualFileName);
      
      let uploadAttempts = 0;
      const maxUploadAttempts = 3;
      let uploadError = null;
      let uploadData = null;
      
      while (uploadAttempts < maxUploadAttempts) {
        try {
          const { data, error } = await supabase.storage
            .from(bucket)
            .upload(actualFileName, file, {
              cacheControl: '3600',
              upsert: true,
            });
            
          if (error) {
            console.error(`Upload attempt ${uploadAttempts + 1} failed:`, error);
            
            if (error.message.includes('row-level security policy')) {
              console.log("RLS policy prevented upload. Using local storage fallback");
              setUseLocalStorageOnly(true);
              return uploadFile(file, bucket, fileName);
            }
            
            uploadError = error;
            uploadAttempts++;
            
            if (uploadAttempts < maxUploadAttempts) {
              await new Promise(resolve => setTimeout(resolve, 1000 * uploadAttempts));
              continue;
            } else {
              throw error;
            }
          }
          
          uploadData = data;
          uploadError = null;
          break;
        } catch (err: any) {
          console.error(`Upload attempt ${uploadAttempts + 1} exception:`, err);
          
          if (err.message && err.message.includes('row-level security policy')) {
            console.log("RLS policy prevented upload. Using local storage fallback");
            setUseLocalStorageOnly(true);
            return uploadFile(file, bucket, fileName);
          }
          
          uploadError = err;
          uploadAttempts++;
          
          if (uploadAttempts < maxUploadAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1000 * uploadAttempts));
          } else {
            throw err;
          }
        }
      }
      
      if (uploadError) {
        throw uploadError;
      }
      
      console.log('Upload successful, data:', uploadData);
      
      const publicUrl = getCorrectPublicUrl(bucket, uploadData.path);
      console.log('Generated public URL:', publicUrl);
      
      return {
        url: publicUrl,
        path: uploadData.path,
        success: true
      };
    } catch (error: any) {
      console.error('Error detail in uploadFile:', error);
      
      setUseLocalStorageOnly(true);
      
      toast({
        title: "Uppladdningsfel",
        description: "Använder lokal lagring istället.",
        variant: "destructive"
      });
      
      return uploadFile(file, bucket, fileName);
    }
  };

  const deleteFile = async (path: string, bucket: string) => {
    try {
      if (path.startsWith('local_') || useLocalStorageOnly()) {
        console.log("Removing local file:", path);
        try {
          const localFiles = JSON.parse(localStorage.getItem('localFiles') || '{}');
          if (localFiles[path]) {
            delete localFiles[path];
            localStorage.setItem('localFiles', JSON.stringify(localFiles));
          }
        } catch (e) {
          console.warn("Could not remove file from localStorage", e);
        }
        return { success: true };
      }
      
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting file:', error);
      return { success: false, error };
    }
  };

  return {
    isInitialized,
    isInitializing,
    initializeStorage,
    uploadFile,
    deleteFile,
    hasValidSupabaseConfig,
    useLocalStorageOnly: useLocalStorageOnly(),
    resetStorage: () => {
      setUseLocalStorageOnly(false);
      setBucketCreationAttempted(false);
    }
  };
};
